import React from 'react';
import { ButtonGroup } from '@material-ui/core';
import { ToggleButtonGroup } from '@material-ui/lab';
import { PlayerData } from '@domain/types/player/player.data';
import { BoolControl, useBool } from '@domain/hooks/use.bool';
import { Loading } from '@components/loading';
import { ButtonArea, Root } from '@screens/players/button.bar/button.bar.styles';
import { PlayerAction } from '@screens/players/button.bar/types/player.action.type';
import { PlayerPropertyToggle } from '@screens/players/button.bar/player.property.toggle';
import { PlayerActionButton } from '@screens/players/button.bar/player.action.button';
import { Dictionary } from '@domain/types/common/dictionary';
import { PlayerDataProps } from '@screens/players/types/player.data.props';
import CreditsDialog from '@screens/players/player.actions/credits/credits.dialog';
import CleanCacheDialog from '@screens/players/player.actions/clean.cache/clean.cache.dialog';
import BlockPlayerDialog from '@screens/players/player.actions/toggles/block.player.dialog';
import SetTesterDialog from '@screens/players/player.actions/toggles/set.tester.dialog';
import ChangePaymentsDialog from '@screens/players/player.actions/toggles/change.payments.dialog';
import ActionLoggingDialog from '@screens/players/player.actions/toggles/action.logging.dialog';
import MetricsDialog from '@screens/players/player.actions/metrics/metrics.dialog';

// TO-DO: uncomment actions that are integrated with the API

const propertyToggles: PlayerAction[] = [
  { id: 'block', icon: 'person_off', text: 'Block/unblock player', component: BlockPlayerDialog },
  { id: 'tester', icon: 'bug_report', text: 'Set/unset as tester', component: SetTesterDialog },
  { id: 'payments', icon: 'paid', text: 'Enable/disable payments', component: ChangePaymentsDialog },
  { id: 'logging', icon: 'receipt_long', text: 'Enable/disable action logging', component: ActionLoggingDialog },
];

const actions: PlayerAction[] = [
  { id: 'credits', icon: 'redeem', text: 'Open credit tools (stats & bonus)', component: CreditsDialog },
  { id: 'metrics', icon: 'analytics', text: 'Open Metrics tools', component: MetricsDialog },

  // { id: 'inbox', icon: 'inbox', text: 'Open Inbox' },
  // { id: 'kickFromChat', icon: 'speaker_notes_off', text: 'Kick from chat rooms' },
  { id: 'cleanCache', icon: 'cleaning_services', text: 'Clean cache', component: CleanCacheDialog },
];

const allActions = propertyToggles.concat(actions);

function getPlayerToggles(player: PlayerData) {
  const playerToggles: string[] = [];
  if (player.toggles.blocked) playerToggles.push('block');
  if (player.toggles.tester) playerToggles.push('tester');
  if (!player.toggles.paymentDisabled) playerToggles.push('payments');
  if (player.toggles.actionLogging) playerToggles.push('logging');
  return playerToggles;
}

interface ButtonBarProps extends PlayerDataProps {
  loading: boolean;
  onChange?(): void;
}

const ButtonBar = ({ player, loading, onChange }: ButtonBarProps) => {
  const playerToggles = getPlayerToggles(player);

  const blockOpen = useBool(false);
  const testerOpen = useBool(false);
  const metricsOpen = useBool(false);
  const paymentsOpen = useBool(false);
  const loggingOpen = useBool(false);
  const creditsOpen = useBool(false);
  const cacheOpen = useBool(false);

  // use id's from 'actions' & 'propertyToggles' as keys
  const actionOpens: Dictionary<BoolControl> = {
    block: blockOpen,
    tester: testerOpen,
    payments: paymentsOpen,
    metrics: metricsOpen,
    logging: loggingOpen,
    credits: creditsOpen,
    cleanCache: cacheOpen,
  };
  return (
    <Root>
      <ButtonArea>
        <ToggleButtonGroup value={playerToggles} aria-label='Player properties'>
          {propertyToggles.map((property) => (
            <PlayerPropertyToggle
              key={property.id}
              data={property}
              disabled={loading}
              onClick={actionOpens[property.id]?.toggle}
              selected={playerToggles.includes(property.id)}
            />
          ))}
        </ToggleButtonGroup>

        <ButtonGroup variant='contained' aria-label='Player actions' color='primary'>
          {actions.map((action) => (
            <PlayerActionButton key={action.id} data={action} disabled={loading} onClick={actionOpens[action.id]?.toggle} />
          ))}
        </ButtonGroup>

        {allActions.map(({ id, component: Component }) => {
          if (!Component) return null;
          return <Component key={id} player={player} open={actionOpens[id]} requestChange={onChange} />;
        })}
      </ButtonArea>

      <Loading loading={loading} bottom />
    </Root>
  );
};

export default ButtonBar;
